import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import {
  ControlValueAccessor, Validator, AbstractControl, ValidationErrors,
  NG_VALUE_ACCESSOR, NG_VALIDATORS,
} from '@angular/forms';
import {
  MAX_PHONES, phoneParts, isValidPhone, formatPhoneAsTyped,
} from '../../utils/phone.util';

/** Mirrors the separators phone.util splits on. */
const SEPARATOR_PRESENT = /[,;]|\s+or\s+/i;

/**
 * Editor for a phone field that may hold more than one number.
 *
 * The stored value is a single comma separated string; this component splits
 * it into a row per number so the value never passes through a control that
 * could truncate it, and formats each row on blur rather than masking
 * keystrokes. Masking caps input at ten digits, which is what made multi
 * number fields lose their second number on save.
 */
@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, Validator {
  /** Numbers the user may add. Existing values above this are still shown. */
  @Input() maxPhones = MAX_PHONES;
  @Input() placeholder = '';
  @Input() inputId: string = null;
  @Input() readonly = false;
  /** Host forms drive their own error styling; mirror it onto the rows. */
  @Input() invalid = false;

  /**
   * Fires after a row has been formatted, so hosts that react to blur (the
   * contact picker's duplicate check, for one) compare the canonical value
   * rather than whatever was mid-typing.
   */
  @Output() blurred = new EventEmitter<void>();

  rows: string[] = [''];
  disabled = false;

  /**
   * Hosts pass their form's submitted flag so numbers that predate the ten
   * digit cap surface when the form is submitted, rather than only after the
   * user happens to click into the field.
   */
  @Input() submitted = false;

  /** Set on blur so the message appears when the user leaves the field. */
  showError = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  /** Hidden once the field is full, so the cap needs no explanation. */
  get canAdd(): boolean {
    return this.editable && this.rows.length < this.maxPhones;
  }

  get editable(): boolean {
    return !this.disabled && !this.readonly;
  }

  get currentlyValid(): boolean {
    return isValidPhone(this.rows.join(', '));
  }

  /** On blur once touched, and on submit for values loaded already bad. */
  get errorVisible(): boolean {
    return this.showError || (this.submitted && !this.currentlyValid);
  }

  // ControlValueAccessor

  writeValue(value: string) {
    // Show every stored number even if there are more than maxPhones: hiding
    // one would be the silent data loss this component exists to prevent.
    const parts = phoneParts(value);
    this.rows = parts.length ? parts : [''];
  }

  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  // Validator

  validate(control: AbstractControl): ValidationErrors {
    return isValidPhone(control.value) ? null : { phone: true };
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChange = fn;
  }

  private onValidatorChange: () => void = () => {};

  // Template handlers

  /**
   * Masks as the user types: caps at ten digits and inserts the parentheses
   * and dash on the way.
   *
   * A value carrying a separator is left alone so a pasted pair survives to
   * be split across rows on blur; masking it here would keep the first number
   * and drop the second.
   */
  onRowInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;

    if (SEPARATOR_PRESENT.test(input.value)) {
      this.rows[index] = input.value;
    } else {
      const caret = this.digitsBeforeCaret(input);
      const masked = formatPhoneAsTyped(input.value);

      this.rows[index] = masked;
      if (input.value !== masked) {
        // Rewriting value would otherwise throw the caret to the end, which
        // makes correcting a digit mid-number impossible.
        input.value = masked;
        this.restoreCaret(input, caret);
      }
    }

    // Clear a previous complaint as soon as they start correcting it.
    this.showError = false;
    this.emit();
  }

  /**
   * Settles the row on the way out. A pasted "916..., 530..." splits across
   * rows here.
   */
  onRowBlur(index: number) {
    const parts = phoneParts(this.rows[index]);

    if (parts.length) {
      this.rows.splice.apply(this.rows, [index, 1].concat(parts as any[]));
    } else {
      this.rows[index] = '';
    }

    // Only judge once the user has left the field, so a half typed number is
    // never flagged mid-keystroke.
    this.showError = !this.currentlyValid;

    this.onTouched();
    this.emit();
    this.blurred.emit();
  }

  /** How many digits precede the caret, the only position that survives a remask. */
  private digitsBeforeCaret(input: HTMLInputElement): number {
    const caret = input.selectionStart || 0;
    return (input.value.slice(0, caret).match(/\d/g) || []).length;
  }

  private restoreCaret(input: HTMLInputElement, digitsBefore: number) {
    let pos = 0;
    let seen = 0;
    while (pos < input.value.length && seen < digitsBefore) {
      if (/\d/.test(input.value.charAt(pos))) {
        seen++;
      }
      pos++;
    }
    input.setSelectionRange(pos, pos);
  }

  /** Empties the row without removing it, so the field stays there to retype into. */
  clearRow(index: number) {
    this.rows[index] = '';
    this.showError = false;
    this.emit();
  }

  addRow() {
    this.rows.push('');
  }

  removeRow(index: number) {
    this.rows.splice(index, 1);
    if (!this.rows.length) {
      this.rows = [''];
    }
    this.emit();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private emit() {
    const filled = this.rows
      .map(row => (row || '').trim())
      .filter(row => row !== '');

    this.onChange(filled.join(', '));
    this.onValidatorChange();
  }
}
